import React, { useState, useEffect } from 'react';
import { ScheduleEvent, BookModel } from '../../../../../models';
import { db } from '../../../../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
    ChevronLeft, CheckSquare, Check, Paperclip, Video, Calculator, Info
} from 'lucide-react';
import ScheduleBookOMR from './ScheduleBookOMR';
import ScheduleBookInfo from './ScheduleBookInfo';
import ScheduleBookLecture from './ScheduleBookLecture';
import ScheduleBookFile from './ScheduleBookFile';

interface StudentScheduleBookDetailProps {
    event: ScheduleEvent;
    onBack: () => void;
    onUpdateStatus: (status: 'pending' | 'completed') => void;
    onPlayVideo?: (url: string, title: string) => void;
}

type DetailTab = 'info' | 'lecture' | 'omr' | 'file';

const ScheduleBookDetail: React.FC<StudentScheduleBookDetailProps> = ({ event, onBack, onUpdateStatus, onPlayVideo }) => {
    const [activeTab, setActiveTab] = useState<DetailTab>('info');
    const [book, setBook] = useState<BookModel | null>(null);
    const [lectureSets, setLectureSets] = useState<any[]>([]);
    const [omrSets, setOmrSets] = useState<any[]>([]);
    const [fileSets, setFileSets] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchAllData = async () => {
            setIsLoading(true);
            try {
                const bookSnap = await getDoc(doc(db, 'books', event.bookId));
                if (bookSnap.exists()) {
                    setBook({ id: bookSnap.id, ...bookSnap.data() } as BookModel);
                }

                const lectSnap = await getDoc(doc(db, 'bookLectures', event.bookId));
                if (lectSnap.exists()) {
                    const data = lectSnap.data();
                    if (data.lectures && (!data.lectureSets || data.lectureSets.length === 0)) {
                        if (data.lectures.length > 0) {
                            setLectureSets([{ id: 'default', title: '기본 강의 목록', lectures: data.lectures }]);
                        } else {
                            setLectureSets([]);
                        }
                    } else {
                        setLectureSets(data.lectureSets || []);
                    }
                }

                const omrSnap = await getDoc(doc(db, 'bookOMR', event.bookId));
                if (omrSnap.exists()) {
                    setOmrSets(omrSnap.data().omrSets || []);
                }

                const fileSnap = await getDoc(doc(db, 'bookFiles', event.bookId));
                if (fileSnap.exists()) {
                    const data = fileSnap.data();
                    if ((data.files || data.pdfs || data.mp3s) && (!data.fileSets || data.fileSets.length === 0)) {
                        const combined = [
                            ...(data.pdfs || []).map((f: any) => ({ ...f, category: 'pdf' })),
                            ...(data.mp3s || []).map((f: any) => ({ ...f, category: 'mp3' })),
                            ...(data.files || [])
                        ];
                        if (combined.length > 0) {
                            setFileSets([{ id: 'default', title: '기본 자료 목록', files: combined }]);
                        } else {
                            setFileSets([]);
                        }
                    } else {
                        setFileSets(data.fileSets || []);
                    }
                }

            } catch (error) {
                console.error("Error fetching detail data:", error);
            } finally {
                setIsLoading(false);
            }
        };

        if (event.bookId) fetchAllData();
    }, [event.bookId]);

    const getSubjectColor = (subject: string) => {
        const colors: Record<string, string> = {
            '국어': 'bg-rose-500', '수학': 'bg-blue-600', '영어': 'bg-indigo-600',
            '한국사': 'bg-amber-700', '사회': 'bg-orange-600', '과학': 'bg-emerald-600',
        };
        return colors[subject] || 'bg-slate-600';
    };

    return (
        <div className="flex flex-col h-full bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden animate-in fade-in duration-500">
            {/* Header Section */}
            <header className="px-5 py-4 border-b border-slate-50 flex items-center justify-between gap-3 shrink-0">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <button onClick={onBack} className="w-9 h-9 flex items-center justify-center bg-slate-50 text-slate-400 rounded-lg hover:bg-slate-100 transition-all shrink-0">
                        <ChevronLeft size={18} />
                    </button>

                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                            <span className={`px-1.5 py-0.5 rounded text-[8px] font-black text-white uppercase tracking-widest ${getSubjectColor(event.subject)}`}>
                                {event.subject}
                            </span>
                            <span className="text-[10px] font-bold text-slate-400 whitespace-nowrap">
                                {new Date(event.date).toISOString().split('T')[0]}
                            </span>
                        </div>
                        <h3 className="text-sm sm:text-base font-black text-slate-800 tracking-tight truncate leading-tight">
                            {event.bookTitle}
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onUpdateStatus(event.status === 'completed' ? 'pending' : 'completed');
                        }}
                        className={`flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-xs font-black transition-all ${event.status === 'completed'
                            ? 'bg-emerald-100 text-emerald-600 ring-2 ring-emerald-50'
                            : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                            }`}
                    >
                        {event.status === 'completed' ? <CheckSquare size={14} /> : <Check size={14} />}
                        {event.status === 'completed' ? '학습 완료됨' : '완료로 표시'}
                    </button>
                </div>
            </header>

            <div className="flex-1 overflow-hidden flex flex-col">
                {/* Tabs Navigation */}
                <nav className="px-5 flex items-center gap-5 border-b border-slate-50 overflow-x-auto no-scrollbar shrink-0">
                    {(['info', 'lecture', 'omr', 'file'] as const).map((tab) => (
                        <button
                            key={tab}
                            onClick={() => {
                                setActiveTab(tab);
                            }}
                            className={`py-3 text-xs font-black uppercase tracking-widest relative transition-all whitespace-nowrap ${activeTab === tab ? 'text-indigo-600' : 'text-slate-400 hover:text-slate-600'
                                }`}
                        >
                            <div className="flex items-center gap-1.5">
                                {tab === 'info' && <Info size={14} />}
                                {tab === 'lecture' && <Video size={14} />}
                                {tab === 'omr' && <Calculator size={14} />}
                                {tab === 'file' && <Paperclip size={14} />}
                                {tab === 'info' ? '기본정보' : tab === 'lecture' ? '인강강의' : tab === 'omr' ? 'OMR' : '자료파일'}
                            </div>
                            {activeTab === tab && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-full" />
                            )}
                        </button>
                    ))}
                </nav>

                {/* Tab Content Area */}
                <div className="flex-1 overflow-y-auto p-5 custom-scrollbar">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center h-full gap-3 py-10">
                            <div className="w-10 h-10 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
                            <p className="text-xs text-slate-400 font-bold">정보를 불러오는 중입니다...</p>
                        </div>
                    ) : (
                        <div>
                            {activeTab === 'info' && (
                                <ScheduleBookInfo
                                    event={event}
                                    book={book}
                                />
                            )}

                            {activeTab === 'lecture' && (
                                <ScheduleBookLecture
                                    lectureSets={lectureSets}
                                    onPlayVideo={onPlayVideo}
                                />
                            )}

                            {activeTab === 'omr' && (
                                <ScheduleBookOMR omrSets={omrSets} event={event} />
                            )}

                            {activeTab === 'file' && (
                                <ScheduleBookFile fileSets={fileSets} />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div >
    );
};

export default ScheduleBookDetail;
